/**
 * localStorage-backed chat history shared by the profile chat widget and the
 * global "Ask Talent AI" widget. Rules are identical for both:
 *   - at most CHAT_MAX_CHATS conversations per storage key
 *   - at most CHAT_MAX_MESSAGES messages per conversation
 *   - both caps evict the OLDEST ("can't go older than that")
 * Best-effort — any storage error (quota, disabled, bad JSON) is swallowed so
 * the chat keeps working with an in-memory conversation.
 *
 * Callers own their storage key: the profile widget scopes it per candidate
 * (`profileChat:v1:<profileId>`), the global widget uses one key
 * (`talentChat:v1`). Message objects are stored whole, so extra per-widget
 * fields (e.g. the global widget's candidate `references`) round-trip intact.
 */
export const CHAT_MAX_CHATS = 5;
// Total entries per chat (user + assistant combined) — ~30 back-and-forth turns.
export const CHAT_MAX_MESSAGES = 60;

export interface ChatHistoryMsg {
  role: "user" | "assistant";
  content: string;
}

export interface StoredChat<M extends ChatHistoryMsg = ChatHistoryMsg> {
  id: string;
  createdAt: number;
  updatedAt: number;
  messages: M[];
}

const isValidMsg = (m: any): boolean =>
  m && (m.role === "user" || m.role === "assistant") && typeof m.content === "string";

export function newChatId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Read + sanitize the stored chats for a key (newest first, caps enforced). */
export function loadChatHistory<M extends ChatHistoryMsg = ChatHistoryMsg>(storageKey: string): StoredChat<M>[] {
  if (!storageKey) return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((c: any) => c && typeof c.id === "string" && Array.isArray(c.messages))
      .map((c: any) => ({
        id: c.id as string,
        createdAt: Number(c.createdAt) || 0,
        updatedAt: Number(c.updatedAt) || 0,
        messages: (c.messages as any[]).filter(isValidMsg).slice(-CHAT_MAX_MESSAGES) as M[],
      }))
      .filter((c) => c.messages.length > 0)
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, CHAT_MAX_CHATS);
  } catch {
    return [];
  }
}

export function persistChatHistory<M extends ChatHistoryMsg = ChatHistoryMsg>(
  storageKey: string,
  chats: StoredChat<M>[],
): void {
  if (!storageKey) return;
  try {
    const trimmed = chats
      .filter((c) => c.messages.length > 0)
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, CHAT_MAX_CHATS)
      .map((c) => ({ ...c, messages: c.messages.slice(-CHAT_MAX_MESSAGES) }));
    localStorage.setItem(storageKey, JSON.stringify(trimmed));
  } catch {
    /* storage full / disabled — history is best-effort */
  }
}

/** Upsert the active chat into the stored history, enforcing caps. Returns the
 *  new (newest-first, capped) list. */
export function saveActiveChat<M extends ChatHistoryMsg = ChatHistoryMsg>(
  storageKey: string,
  chatId: string,
  messages: M[],
): StoredChat<M>[] {
  const history = loadChatHistory<M>(storageKey);
  const now = Date.now();
  const existing = history.find((c) => c.id === chatId);
  const updated: StoredChat<M> = {
    id: chatId,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    messages: messages.slice(-CHAT_MAX_MESSAGES),
  };
  const next = [updated, ...history.filter((c) => c.id !== chatId)]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, CHAT_MAX_CHATS);
  persistChatHistory(storageKey, next);
  return next;
}

export function relativeTime(ts: number): string {
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

export function chatPreview(chat: StoredChat<any>): string {
  const firstUser = chat.messages.find((m: ChatHistoryMsg) => m.role === "user");
  const text = (firstUser?.content || chat.messages[0]?.content || "Conversation").replace(/\s+/g, " ").trim();
  return text.length > 42 ? `${text.slice(0, 42)}…` : text;
}
